import React, { useEffect, useState } from "react";
import { confirmable } from "react-confirm";
import { Modal, Col, Input, Row, Select, Spin, Switch } from "antd";
import { isEmpty } from "utils/GlobalFunctions";
import ProductImageWidget from "pages/RecipeListPage/AddRecipeListPage/ProductImageWidget";
import "pages/RecipeListPage/AddRecipeListPage/AddRecipeListPage.css";
import "./ShopDialog.css";
import ProductWYSIWYGEditor from "components/ProductPage/ProductWYSIWYGEditor/ProductWYSIWYGEditor";
import VariantsEditor from "./VariantsEditor";
import { apiGetAllShopCategories } from "services/shopCategoryService";

const { Option } = Select;

const stripHtml = (html) => {
  const tmp = document.createElement("DIV");
  tmp.innerHTML = html || "";
  return tmp.textContent || tmp.innerText || "";
};

// Load variants from a DB record, coercing numeric fields and keeping ids.
const normalizeRecordVariants = (variants) => {
  if (!Array.isArray(variants)) return [];
  return variants.map((v) => ({
    id: v.id,
    name: v.name || "",
    weight: v.weight || "",
    sku: v.sku || "",
    price: v.price !== undefined && v.price !== null && v.price !== "" ? Number(v.price) : 0,
    stock_qty:
      v.stock_qty !== undefined && v.stock_qty !== null && v.stock_qty !== ""
        ? Number(v.stock_qty)
        : 0,
    status: v.status !== undefined ? v.status : "1",
  }));
};

function AddProductDialog(props) {
  const { show, proceed, record, title } = props;

  const [data, setData] = useState({});
  const [variants, setVariants] = useState([]);
  const [categories, setCategories] = useState([]);
  const [errorField, setErrorField] = useState([]);
  const [isBusy, setIsBusy] = useState(false);

  useEffect(() => {
    setIsBusy(true);
    apiGetAllShopCategories()
      .then((list) => {
        setCategories(Array.isArray(list) ? list : []);
        setIsBusy(false);
      })
      .catch(() => {
        setCategories([]);
        setIsBusy(false);
      });
  }, []);

  useEffect(() => {
    if (record !== undefined) {
      setData({ ...record });
      setVariants(normalizeRecordVariants(record.variants));
    } else {
      setData({ status: "0" });
      setVariants([]);
    }
  }, [record?.id, record?.thumbnail_image]);

  const onViewMode = (checked) => {
    setData({ ...data, status: checked === true ? "1" : "0" });
  };

  const onChangeValue = (field, value) => {
    setData({ ...data, [field]: value });
    setErrorField(errorField.filter((x) => x !== field));
  };

  const validateFields = () => {
    var errorList = Array();
    errorList = isEmpty(data, "name", errorList);
    // At least one variant with a weight/size label
    const hasValidVariant =
      Array.isArray(variants) &&
      variants.some((v) => (v.weight && v.weight.trim() !== "") || (v.name && v.name.trim() !== ""));
    if (!hasValidVariant) {
      errorList = [...errorList, "variants"];
    }
    setErrorField([...errorList]);
    return errorList.length == 0;
  };

  return (
    <Modal
      open={show}
      destroyOnClose={true}
      onCancel={() => proceed(null)}
      closable={true}
      maskClosable={false}
      title={title}
      width={720}
      className="shop-modal"
      okText="Save"
      onOk={() => {
        if (validateFields() === false) return;
        // Drop empty variant rows (no weight and no name)
        const cleanedVariants = variants.filter(
          (v) => (v.weight && v.weight.trim() !== "") || (v.name && v.name.trim() !== "")
        );
        proceed({ ...data, variants: cleanedVariants });
      }}
    >
      <Spin spinning={isBusy}>
        <div>
          <div className="shop-switch-row">
            <label className="shop-field-label" style={{ marginBottom: 0 }}>
              Images
            </label>
            <Switch
              checkedChildren="Show"
              unCheckedChildren="Hide"
              onChange={onViewMode}
              checked={data["status"] === "1" ? true : false}
            />
          </div>
          <ProductImageWidget
            field={"thumbnail_image"}
            productInfo={data}
            setProductInfo={setData}
            errorField={errorField}
            setErrorField={setErrorField}
          />

          <Row gutter={16} style={{ marginTop: "18px" }}>
            <Col xs={24} sm={24} md={12} lg={12} xl={12}>
              <div className="shop-field">
                <label className="shop-field-label">
                  Name<span className="req">*</span>
                </label>
                <Input
                  placeholder="e.g. Premium Whole Leaf Teebica"
                  value={stripHtml(data["name"]) || ""}
                  status={errorField.includes("name") ? "error" : ""}
                  onChange={(e) => onChangeValue("name", e.target.value)}
                />
              </div>
            </Col>
            <Col xs={24} sm={24} md={12} lg={12} xl={12}>
              <div className="shop-field">
                <label className="shop-field-label">Category</label>
                <Select
                  style={{ width: "100%" }}
                  placeholder="Select a category"
                  allowClear
                  value={data["category_id"] ? String(data["category_id"]) : undefined}
                  onChange={(value) => onChangeValue("category_id", value)}
                >
                  {categories.map((cat) => (
                    <Option key={cat.id} value={String(cat.id)}>
                      {stripHtml(cat.name)}
                    </Option>
                  ))}
                </Select>
              </div>
            </Col>
          </Row>

          <div className="shop-field">
            <label className="shop-field-label">Description</label>
            <ProductWYSIWYGEditor
              placeholder="Enter description..."
              field={"description"}
              productInfo={data}
              setProductInfo={setData}
              errorField={errorField}
              setErrorField={setErrorField}
            />
          </div>

          <VariantsEditor
            variants={variants}
            setVariants={setVariants}
            errorField={errorField}
            setErrorField={setErrorField}
          />
        </div>
      </Spin>
    </Modal>
  );
}

export default confirmable(AddProductDialog);
